import { action, computed, makeObservable } from 'mobx'

import { ISiteLocation, SitePermitStatus } from '~/client/graph'
import FormReportFilterType, {
  formReportFilterTypes,
  getFormReportFilterTypeCaption,
} from '~/client/src/desktop/enums/FormReportFilterType'
import DesktopEventsStore from '~/client/src/desktop/stores/EventStore/DesktopEvents.store'
import { UNASSIGNED_FILTER_OPTION } from '~/client/src/shared/components/Deliveries/DeliveriesView.store'
import { LocalStorageKey } from '~/client/src/shared/enums/LocalStorageKey'
import { getFormStatusDisplayName } from '~/client/src/shared/localization/enumDisplayTexts'
import Workflow from '~/client/src/shared/models/Workflow'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import ProjectMembersStore from '~/client/src/shared/stores/domain/ProjectMembers.store'
import ScannersStore from '~/client/src/shared/stores/domain/Scanners.store'
import TagsStore from '~/client/src/shared/stores/domain/Tags.store'
import UserProjectsStore from '~/client/src/shared/stores/domain/UserProjects.store'
import { RESET_ALL_FILTERS } from '~/client/src/shared/stores/EventStore/eventConstants'
import { enumToList } from '~/client/src/shared/utils/converters'
import { LOCATION_SEPARATOR } from '~/client/src/shared/utils/usefulStrings'

import ReportsStore from '../../../../Reports.store'
import DeliveryReportListStore from '../../../ReportsList/DeliveryReport/DeliveryReportList.store'
import FormReportListStore from '../../../ReportsList/FormReportList/FormReportList.store'
import ScanStationListStore from '../../../ReportsList/ScanStation/ScanStationList.store'
import BaseReportsFilterStore, { ISourceMap } from './BaseReportsFilter.store'
import ReportsFilterStore from './ReportsFilter.store'

export default class FormReportFilterStore extends ReportsFilterStore {
  public constructor(
    eventsStore: DesktopEventsStore,
    instancesStore: ReportsStore,
    instancesListStore:
      | ScanStationListStore
      | FormReportListStore
      | DeliveryReportListStore,
    onShowChanged: (isShown: boolean, filterType: string) => void,
    scannersStore: ScannersStore,
    companiesStore: CompaniesStore,
    projectMembersStore: ProjectMembersStore,
    userProjectsStore: UserProjectsStore,
    locationAttributesStore: LocationAttributesStore,
    tagsStore: TagsStore,
  ) {
    super(
      eventsStore,
      instancesStore,
      instancesListStore,
      onShowChanged,
      scannersStore,
      companiesStore,
      projectMembersStore,
      userProjectsStore,
      locationAttributesStore,
      tagsStore,
    )

    makeObservable(this)
  }

  protected get availableInstances(): Workflow[] {
    return this.instancesStore.allFormReportPermits
  }

  @computed
  public get filterStoresByTypeMap(): {
    [filterType: string]: BaseReportsFilterStore
  } {
    const map: { [filterType: string]: BaseReportsFilterStore } = {}

    formReportFilterTypes.forEach(filterType => {
      const { appState } = this.eventsStore

      map[filterType] = new BaseReportsFilterStore(
        filterType,
        appState,
        this.sourceMapByFilterTypeMap[filterType],
        this.instancesListStore,
        this.onShowChanged,
        this.fieldsMap(),
        getFormReportFilterTypeCaption,
        this.getOptionName,
        LocalStorageKey.FormReportReportFilter,
      )
    })
    return map
  }

  @action.bound
  public resetAllFilters() {
    formReportFilterTypes.forEach(type =>
      localStorage.removeItem(
        `${this.eventsStore.appState.activeProject.id}_${LocalStorageKey.FormReportReportFilter}_${type}`,
      ),
    )
    this.eventsStore.dispatch(RESET_ALL_FILTERS)
  }

  @computed
  protected get sourceMapByFilterTypeMap(): {
    [filterType: string]: ISourceMap
  } {
    const maps = formReportFilterTypes.reduce((acc, filterType) => {
      acc[filterType] = this.getDefaultSourceMapByType(filterType)
      return acc
    }, {})

    this.availableInstances.forEach(instance => {
      Object.keys(maps).forEach(filterType => {
        const map = maps[filterType]
        let optionIds: string[] = []

        switch (filterType) {
          case FormReportFilterType.COMPANY:
            optionIds = instance.companyIds?.length
              ? instance.companyIds
              : [UNASSIGNED_FILTER_OPTION]
            break
          case FormReportFilterType.STATUS:
            optionIds = [instance.status || UNASSIGNED_FILTER_OPTION]
            break
          case FormReportFilterType.LOCATION:
            optionIds = this.getLocationValues(instance.locations)
            break
          case FormReportFilterType.EQUIPMENT:
            optionIds = this.getLocationValues(instance.equipment)
            break
        }

        optionIds.forEach(optionId => {
          if (!map[optionId]) {
            optionId = UNASSIGNED_FILTER_OPTION
          }

          map[optionId].push(instance.id)
        })
      })
    })
    return maps
  }

  protected getDefaultSourceMapByType(type: FormReportFilterType) {
    let sourceList: string[] = []

    switch (type) {
      case FormReportFilterType.COMPANY:
        sourceList = [
          ...this.companiesStore.allCompaniesIds,
          UNASSIGNED_FILTER_OPTION,
        ]
        break
      case FormReportFilterType.STATUS:
        sourceList = [...enumToList(SitePermitStatus), UNASSIGNED_FILTER_OPTION]
        break
      case FormReportFilterType.LOCATION:
        sourceList = Array.from(
          this.locationAttributesStore.allLocationsWithoutEquipment.reduce(
            (acc, location) => {
              acc.add(`${location.type}${LOCATION_SEPARATOR}${location.id}`)
              return acc
            },
            new Set([UNASSIGNED_FILTER_OPTION]),
          ),
        )
        break
      case FormReportFilterType.EQUIPMENT:
        sourceList = Array.from(
          this.locationAttributesStore.offloadingEquipmentsStore.list.reduce(
            (acc, location) => {
              acc.add(`${location.type}${LOCATION_SEPARATOR}${location.id}`)
              return acc
            },
            new Set([UNASSIGNED_FILTER_OPTION]),
          ),
        )
        break
    }

    return sourceList.reduce((acc, optionId) => {
      acc[optionId] = []
      return acc
    }, {})
  }

  protected fieldsMap() {
    return this.eventsStore.appState.formReportFilters.fieldsMap
  }

  protected getOptionName = (option: string, filterType: string): string => {
    if (filterType === FormReportFilterType.COMPANY) {
      return this.companiesStore.getCompanyNameById(
        option,
        UNASSIGNED_FILTER_OPTION,
      )
    }
    if (filterType === FormReportFilterType.STATUS) {
      return getFormStatusDisplayName(option as SitePermitStatus) || option
    }
    if (
      [FormReportFilterType.LOCATION, FormReportFilterType.EQUIPMENT].includes(
        filterType as FormReportFilterType,
      )
    ) {
      const [, attributeId] = option.split(LOCATION_SEPARATOR)

      // By id: the kind half names the store the location sat in when the key was
      // written, which a re-kind changes and a project-added kind has none of (STX1-597)
      return (
        this.tagsStore.getLocationByIdWithDeleted(attributeId)?.name || option
      )
    }

    return option
  }

  /**
   * Keyed by the live kind: the options come from live locations (getDefaultSourceMapByType)
   * but these values from the permit's SiteLocation snapshot. A snapshot taken before a
   * re-kind names no option, and the permit would count as Unassigned (STX1-597). A location
   * not loaded or deleted keeps its snapshot kind, and lands in Unassigned as before.
   */
  private getLocationValues = (locations: ISiteLocation[]): string[] => {
    return locations?.length
      ? locations.map(
          l =>
            `${this.locationAttributesStore.getById(l.id)?.type || l.type}${LOCATION_SEPARATOR}${l.id}`,
        )
      : [UNASSIGNED_FILTER_OPTION]
  }
}
